import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema
const createGroupSchema = z.object({
  name: z.string().min(1, 'Group name is required'),
  description: z.string().optional()
});
type CreateGroupFormValues = z.infer<typeof createGroupSchema>;
interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateGroupFormValues) => Promise<void>;
}
export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onCreate
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<CreateGroupFormValues>({
    resolver: zodResolver(createGroupSchema),
    defaultValues: {
      name: '',
      description: ''
    }
  });
  const onSubmit = async (data: CreateGroupFormValues) => {
    try {
      await onCreate(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-1139">
      <AnimatePresence data-id="element-1140">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1141">
            <Dialog.Overlay asChild data-id="element-1142">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1143">
                <Dialog.Content asChild data-id="element-1144">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1145">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1146">
                      <div className="flex items-center gap-3" data-id="element-1147">
                        <div className="w-10 h-10 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-1148">
                          <div size={20} data-id="element-1149" />
                        </div>
                        <div data-id="element-1150">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1151">
                            Create New Group
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1152">
                            Create a flexible group for remedial batches or
                            practice sets
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1153">
                        <XIcon size={20} data-id="element-1154" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1155">
                      <div className="p-6 space-y-5" data-id="element-1156">
                        {/* Info Banner */}
                        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl" data-id="element-1157">
                          <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1158">
                            <span className="font-semibold" data-id="element-1159">Groups</span> are
                            flexible, educator-created sets for remedial
                            batches, practice groups, and short-term cohorts.
                            They're separate from official organization
                            classrooms.
                          </p>
                        </div>

                        {/* Name Field */}
                        <div className="space-y-1.5" data-id="element-1160">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1161">
                            Group Name <span className="text-red-500" data-id="element-1162">*</span>
                          </label>
                          <input {...register('name')} placeholder="e.g. Remedial Math Group A" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.name ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1163" />
                          {errors.name && <p className="text-xs text-red-500 font-medium" data-id="element-1164">
                              {errors.name.message}
                            </p>}
                        </div>

                        {/* Description Field */}
                        <div className="space-y-1.5" data-id="element-1165">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1166">
                            Description{' '}
                            <span className="text-text-tertiary font-normal" data-id="element-1167">
                              (Optional)
                            </span>
                          </label>
                          <textarea {...register('description')} placeholder="Brief description of the group's purpose" rows={3} className="w-full px-3 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F] transition-all resize-none" data-id="element-1168" />
                        </div>

                        {/* Actions */}
                        <div className="pt-4 flex items-center gap-3" data-id="element-1169">
                          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1170">
                            Cancel
                          </button>
                          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1171">
                            {isSubmitting ? <>
                                <Loader2Icon size={16} className="animate-spin" data-id="element-1172" />
                                Creating...
                              </> : 'Create Group'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};